import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Tabs, 
  Tab, 
  Typography, 
  TextField, 
  Switch, 
  FormControlLabel, 
  Button, 
  Paper, 
  Grid,
  Alert,
  CircularProgress
} from '@mui/material';
import { api } from '../../config/api.config';

const PlatformPanel = ({ platform, value, index }) => {
  const [data, setData] = useState({
    minVersion: '',
    latestVersion: '',
    forceUpdate: false,
    storeUrl: ''
  });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (value === index) {
      fetchVersion();
    }
  }, [value, index]);

  const fetchVersion = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/version/${platform}`);
      if (res.data.success && res.data.data) {
        // 데이터가 없는 경우를 대비한 기본값 처리
        setData({
          minVersion: res.data.data.minVersion || '',
          latestVersion: res.data.data.latestVersion || '',
          forceUpdate: res.data.data.forceUpdate || false,
          storeUrl: res.data.data.storeUrl || ''
        });
      }
    } catch (err) {
      console.error(err);
      setMsg({ type: 'error', text: '버전 정보를 불러오는데 실패했습니다.' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (prop) => (event) => {
    setData({ ...data, [prop]: event.target.value });
  };

  const handleSwitchChange = (prop) => (event) => {
    setData({ ...data, [prop]: event.target.checked });
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setMsg(null);
      await api.post('/version/update', {
        platform,
        ...data
      });
      setMsg({ type: 'success', text: '성공적으로 저장되었습니다.' });
    } catch (err) {
      console.error(err);
      setMsg({ type: 'error', text: '저장에 실패했습니다. ' + (err.response?.data?.message || err.message) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div role="tabpanel" hidden={value !== index} style={{ padding: '20px' }}>
      {value === index && (
        <Paper elevation={3} sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
            {platform === 'android' ? 'Android' : 'iOS'} 버전 설정
          </Typography>

          {msg && (
            <Alert severity={msg.type} sx={{ mb: 2 }}>
              {msg.text}
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="최소 지원 버전 (필수)"
                value={data.minVersion}
                onChange={handleChange('minVersion')}
                placeholder="예: 1.0.0"
                helperText="이 버전 미만의 사용자는 강제 업데이트 대상이 됩니다."
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="최신 버전 (표시용)"
                value={data.latestVersion}
                onChange={handleChange('latestVersion')}
                placeholder="예: 1.1.0"
                helperText="앱 내에 최신 버전임을 알릴 때 사용됩니다."
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={data.forceUpdate}
                    onChange={handleSwitchChange('forceUpdate')}
                    color="error"
                  />
                }
                label="전체 강제 업데이트"
              />
              <Typography variant="caption" display="block" color="textSecondary">
                버전과 상관없이 모든 사용자에게 업데이트를 강제합니다.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="스토어 URL (선택사항)"
                value={data.storeUrl}
                onChange={handleChange('storeUrl')}
                placeholder="https://..."
                helperText="기본 스토어 링크 대신 사용할 URL이 있다면 입력하세요."
              />
            </Grid>

            <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleSave}
                disabled={loading}
                size="large"
              >
                {loading ? <CircularProgress size={24} /> : '저장하기'}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}
    </div>
  );
};

const VersionManage = () => {
  const [value, setValue] = useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        앱 버전 관리
      </Typography>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={value} onChange={handleChange} aria-label="platform tabs">
          <Tab label="Android" />
          <Tab label="iOS" />
        </Tabs>
      </Box>
      
      <PlatformPanel platform="android" value={value} index={0} />
      <PlatformPanel platform="ios" value={value} index={1} />
    </Box>
  );
};

export default VersionManage;

